import { type ElementSize, watchImmediate } from '@vueuse/core';
import { type RenderingContext } from './renderingState';
import { type StopHandle } from '@/util';

/**
 * Apply a viewport size to the rendering context's camera and reactive state.
 * Called by the scene runtime's resize callback (renderer.setSize is owned by
 * the runtime); this function is responsible only for camera frustum math and
 * for surfacing the viewport size to reactive consumers.
 */
export function applyCameraViewport(context: RenderingContext, size: ElementSize): void {
    const { width, height } = size;
    const frustumHeight = Math.max(context.state.camera.height, 1);
    const frustumWidth = frustumHeight * width / Math.max(height, 1);
    context.camera.left = -frustumWidth / 2;
    context.camera.right = frustumWidth / 2;
    context.camera.top = frustumHeight / 2;
    context.camera.bottom = -frustumHeight / 2;
    context.camera.updateProjectionMatrix();
    context.state.viewportSize = { width, height };
}

/**
 * Reapply viewport sizing whenever the orthographic frustum height changes.
 * Container-resize is handled by the scene runtime (see {@link applyCameraViewport}).
 */
export function updateViewport(context: RenderingContext): StopHandle {
    return watchImmediate(
        () => context.state.camera.height,
        () => applyCameraViewport(context, {
            width: context.canvas.width,
            height: context.canvas.height,
        }),
    );
}
